import { createSignal, Show } from "solid-js";
import { linesEntered } from "../../boundaries/user-list-input.parser";
import { GovernEditorActions } from "./GovernEditorActions";
import { isAssigned, validReviewSchedule } from "./governStatus";
import type { GovernReviewSchedule, GovernWorkItem } from "./governTypes";

export function GovernReworkEditor(props: {
  work: GovernWorkItem;
  onSave: (reason: string, owner: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = createSignal(props.work.reworkReason);
  const [owner, setOwner] = createSignal(
    props.work.reworkOwner || props.work.roles.deliveryOwner,
  );
  const [error, setError] = createSignal<string | null>(null);

  return (
    <form
      class="govern-editor-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!reason().trim() || !isAssigned(owner())) {
          setError("Record a rework reason and responsible owner.");
          return;
        }
        props.onSave(reason().trim(), owner().trim());
      }}
    >
      <p>
        Failed checks and existing evidence will remain attached to this work.
      </p>
      <Show when={error()}>{(message) => <p role="alert">{message()}</p>}</Show>
      <label>
        <span>Rework reason</span>
        <textarea
          rows={5}
          value={reason()}
          onInput={(event) => setReason(event.currentTarget.value)}
        />
      </label>
      <label>
        <span>Responsible owner or team</span>
        <input
          value={owner()}
          onInput={(event) => setOwner(event.currentTarget.value)}
        />
      </label>
      <GovernEditorActions onCancel={props.onCancel} />
    </form>
  );
}

export function GovernScheduleEditor(props: {
  work: GovernWorkItem;
  onSave: (schedule: GovernReviewSchedule) => void;
  onCancel: () => void;
}) {
  const current = props.work.reviewSchedule;
  const [date, setDate] = createSignal(current?.nextReviewDate ?? "");
  const [owner, setOwner] = createSignal(
    current?.reviewOwner || props.work.roles.reviewOwner,
  );
  const [reason, setReason] = createSignal(current?.reason ?? "");
  const [triggers, setTriggers] = createSignal(
    current?.triggers.join("\n") ?? "",
  );
  const [mustRemainTrue, setMustRemainTrue] = createSignal(
    current?.mustRemainTrue ?? "",
  );
  const [error, setError] = createSignal<string | null>(null);

  const save = (event: SubmitEvent) => {
    event.preventDefault();
    const schedule: GovernReviewSchedule = {
      nextReviewDate: date(),
      reviewOwner: owner().trim(),
      reason: reason().trim(),
      triggers: linesEntered(triggers()),
      mustRemainTrue: mustRemainTrue().trim(),
    };
    if (!validReviewSchedule(schedule)) {
      setError(
        "Record a date, review owner, reason and at least one trigger or condition.",
      );
      return;
    }
    props.onSave(schedule);
  };

  return (
    <form class="govern-editor-form" onSubmit={save}>
      <p>
        No default cadence is assumed. Record the review this work genuinely
        needs.
      </p>
      <Show when={error()}>{(message) => <p role="alert">{message()}</p>}</Show>
      <label>
        <span>Next review date</span>
        <input
          type="date"
          value={date()}
          onInput={(event) => setDate(event.currentTarget.value)}
        />
      </label>
      <label>
        <span>Review owner</span>
        <input
          value={owner()}
          onInput={(event) => setOwner(event.currentTarget.value)}
        />
      </label>
      <label>
        <span>Reason for review</span>
        <textarea
          rows={3}
          value={reason()}
          onInput={(event) => setReason(event.currentTarget.value)}
        />
      </label>
      <label>
        <span>Trigger events, one per line</span>
        <textarea
          rows={4}
          value={triggers()}
          onInput={(event) => setTriggers(event.currentTarget.value)}
        />
      </label>
      <label>
        <span>What must still remain true</span>
        <textarea
          rows={3}
          value={mustRemainTrue()}
          onInput={(event) => setMustRemainTrue(event.currentTarget.value)}
        />
      </label>
      <GovernEditorActions onCancel={props.onCancel} />
    </form>
  );
}
